import {
  ApiError, get, post,
  type ImportBatchResult, type ImportOpened, type ImportPreview, type ImportResult,
} from '../api.js';
import { fileLines } from '../lib/lines.js';
import {
  ago, button, chip, clear, drawer, el, empty, field, input, mono, panel, row, select, skeleton,
  table, toast,
} from '../ui.js';

interface Source { id: string; name: string; kind: string }

/** What happened to one file in the queue, kept so a run of ten reads back. */
interface Outcome {
  name: string;
  kind: 'done' | 'stopped' | 'refused' | 'failed';
  detail: string;
}

/** The choices made on the commit screen, carried to the next file in a run. */
interface Choice { sourceId: string; onDuplicate: string }

/**
 * What the run should do after a file: take the next one, stop entirely, or
 * stop because the file painted the final screen and it must not be overwritten.
 */
type Step = 'next' | 'abandon' | 'landed';

/**
 * How much of the head of a file the preview is built from. Big enough that the
 * column mapping and the reject rate are representative, small enough that the
 * operator waits a moment rather than a minute.
 */
const PREVIEW_ROWS = 1000;
/** Slack above the header for export banners and blank rows. */
const PREVIEW_SLACK = 30;
/** The preview body must stay well under the API's own file ceiling. */
const PREVIEW_MAX_BYTES = 4 * 1024 * 1024;

/**
 * One batch: a thousand rows, or half a megabyte, whichever comes first.
 *
 * The binding cost is per-row work on the server — a duplicate lookup, an
 * insert, a consent artifact, a scoring pass and a compliance gate for every
 * row — not the size of the body. A thousand rows keeps one request to a
 * second or two, so the progress bar moves visibly and no proxy times the
 * request out; the body that carries them is around two hundred kilobytes,
 * which is a fiftieth of the router's ceiling. That headroom is the point: even
 * a sixty-column vendor file cannot approach the limit, so the limit never has
 * to be raised.
 */
/*
 * WHY THIS IS 200 AND NOT 1000
 *
 * The size that matters is not the request body, it is the TIME the server
 * spends on it. Every row is deduped, scored, and checked against the
 * compliance rules, and the API runs as a serverless function with a hard
 * execution limit. At a thousand rows a batch simply could not finish: a real
 * 2,898-row import died with a 504 after writing 790 people, retried the same
 * oversized batch three times, hit the same wall each time, and cancelled
 * itself.
 *
 * 790 rows in roughly ten seconds puts the throughput near eighty rows a
 * second, so two hundred is about two and a half seconds of work — comfortably
 * inside any of these limits, with room for a slow database day.
 *
 * The trade is more round trips on a big file, which costs a few seconds of
 * wall clock. An import that finishes slowly beats one that does not finish.
 */
const BATCH_ROWS = 200;
const BATCH_BYTES = 512 * 1024;

const OK_EXT = /\.(csv|tsv|tab|txt|psv)$/i;

/* ------------------------------------------------- what the history says -- */

/**
 * The parts of an import that answer "what happened to my file".
 *
 * `GET /v1/imports` returns the whole stored record — the header it read, the
 * column mapping it settled on, the counters the stream keeps in its summary
 * map — and these are the fields this screen actually explains. A run that has
 * just finished can hand over the same shape from its own finish result, so
 * both routes into the drawer are one function.
 */
interface ImportFacts {
  id: string;
  filename: string;
  /**
   * 'preview' | 'streaming' | 'committed' | 'cancelled' | 'failed' | 'reverted'.
   * Deliberately a bare string: a status added on the server after this build
   * shipped must still render as itself rather than as a blank chip.
   */
  status: string;
  totalRows: number;
  inserted: number;
  merged: number;
  rejected: number;
}

/** One row of `GET /v1/imports`. The API returns them newest first. */
interface ImportRecord extends ImportFacts {
  byteSize: number;
  createdAt: string;
  committedAt: string | null;
}

/** One row of `GET /v1/imports/:id/rejects`. The API returns them by row number. */
interface RejectRow {
  rowNumber: number;
  /** The row as the importer understood it: firstName, lastName, phone, email, state, zip. */
  raw: Record<string, string>;
  issues: Array<{ level: string; field: string; message: string }>;
}

/** How many refused rows are painted at once. A file can refuse tens of thousands. */
const REJECT_PAGE = 100;
/** How many imports the history shows before it has to be asked for the rest. */
const HISTORY_ROWS = 8;
const HISTORY_TITLE = 'Every list you have imported';

interface StatusWords {
  /** The chip on the history row. */
  label: string;
  chip: string;
  box: string;
  headline: string;
  blurb: string;
}

/**
 * What each import status means, said once, in the words of the person who
 * owns the file rather than the ones the database uses.
 *
 * `streaming` and `cancelled` carry the most weight. An import in either state
 * wrote some of a file and stopped, and the rows it never reached are not
 * refused rows — they were never read at all, so they appear nowhere. That
 * distinction is the difference between "four thousand rows were bad" and
 * "four thousand rows were never sent", and nothing else on the screen says it.
 */
const STATUS: Record<string, StatusWords> = {
  committed: {
    label: 'finished', chip: 'good', box: 'okbox',
    headline: 'This import ran to the end.',
    blurb: 'Every row in the file was read and accounted for — added, merged into somebody you already had, '
      + 'or refused. Nothing was dropped without being written down.',
  },
  streaming: {
    label: 'never finished', chip: 'crit', box: 'warnbox',
    headline: 'This import was never closed.',
    blurb: 'It started writing and then stopped answering — a closed tab, a lost connection, a machine that '
      + 'went to sleep. What had already been sent is in your book and is counted above. The rest of the file '
      + 'was never read, so it was never refused either and does not appear below. Import the same file again '
      + 'to bring in the remainder: anyone already here merges rather than doubling up.',
  },
  preview: {
    label: 'not imported', chip: '', box: 'note',
    headline: 'This file was previewed and nothing more.',
    blurb: 'Nobody pressed import, so not one row was written. Nothing from this file is in your book, and the '
      + 'counts it was previewed with are not results.',
  },
  cancelled: {
    label: 'stopped', chip: 'warn', box: 'note',
    headline: 'This import was stopped part way.',
    blurb: 'What had already been written was kept and is counted above — no batch was left half done. The rows '
      + 'after the point it stopped were never read, so they are not below. Import the file again to finish it.',
  },
  failed: {
    label: 'failed', chip: 'crit', box: 'warnbox',
    headline: 'This import did not finish.',
    blurb: 'Whatever had been written before it stopped was kept and is counted above. The rest of the file was '
      + 'never read. Import it again to bring in the remainder.',
  },
  reverted: {
    label: 'reverted', chip: 'warn', box: 'note',
    headline: 'This import was undone.',
    blurb: 'The leads it added were taken back out again.',
  },
};

function statusOf(status: string): StatusWords {
  return STATUS[status] ?? {
    label: status, chip: '', box: 'note',
    headline: `This import is marked “${status}”.`,
    blurb: 'This screen has no words for that state yet. The counts above are the server’s own.',
  };
}

/**
 * The refusal reasons in the words of somebody who has to go and fix the file,
 * keyed by the field the importer failed the row on.
 *
 * A reason this screen has never been taught falls back to the importer's own
 * message, which is shown on every row regardless — the plain-English heading
 * is an addition to what the server said, never a replacement for it.
 */
const REASON: Record<string, { title: string; fix: string }> = {
  phone: {
    title: 'No usable phone number',
    fix: 'A ten-digit US number is the one thing a lead cannot be worked without. Blank cells, foreign numbers '
      + 'and numbers with digits missing all land here. If a whole file lands here, check the column mapping on '
      + 'the preview screen first — the number may be in a column this import did not read as the phone.',
  },
  state: {
    title: 'No state, and the ZIP did not give one',
    fix: 'The state is what says which calling hours apply to that person, so a lead without one cannot be '
      + 'dialled at all. A valid ZIP code is usually enough on its own — the state is worked back out of it.',
  },
};

interface Reason { title: string; fix: string; count: number }

/**
 * The refused rows gathered by the reason they were refused, commonest first.
 *
 * Counted over every reject the route returned rather than over the page that
 * happens to be on screen, which is the entire point: it is what turns four
 * thousand refused lines into "three thousand nine hundred of them have no
 * phone number, and here is where that column went wrong".
 */
function reasonsOf(rows: RejectRow[]): Reason[] {
  const by = new Map<string, Reason>();
  for (const r of rows) {
    for (const issue of r.issues) {
      // Only an error refuses a row; warnings ride along on rows that were kept
      // as well, and counting them here would explain a rejection with
      // something that did not cause it.
      if (issue.level !== 'error') continue;
      const known = REASON[issue.field];
      // Grouped by field where there are words for it — two differently
      // malformed numbers are one reason — and by the message itself where
      // there are not, since two unknown messages are two different problems.
      const key = known ? `f:${issue.field}` : `m:${issue.message}`;
      const seen = by.get(key);
      if (seen) { seen.count += 1; continue; }
      by.set(key, { title: known?.title ?? issue.message, fix: known?.fix ?? '', count: 1 });
    }
  }
  return [...by.values()].sort((a, b) => b.count - a.count);
}

/**
 * Import is a three-step wizard on purpose: choose the files, read what will
 * happen to them, then commit. Nothing is written until the third step, so the
 * person always knows what they are agreeing to — and that ordering survives
 * however large the file is, because the preview is taken from the head of it
 * before a single row is streamed.
 */
export function importView(go: (p: string) => void): HTMLElement {
  const host = el('div', { class: 'importer' });
  let sources: Source[] = [];
  let queue: File[] = [];
  const outcomes: Outcome[] = [];
  const choice: Choice = { sourceId: '', onDuplicate: 'merge' };
  /** False while a file is being previewed or streamed; drops then queue up. */
  let idle = true;
  /** The agency's imports, newest first. Null until read, and again once a run has changed them. */
  let history: ImportRecord[] | null = null;
  /** Guards two loads landing out of order — the later one wins. */
  let historySeq = 0;
  /** True once somebody has asked for the whole list rather than the head of it. */
  let historyOpen = false;

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Leads' }),
    el('h1', { text: 'Import a list' }),
    el('p', { text: 'Drop your files anywhere on this screen — as many as you like, however many rows they hold. You will see exactly what happens to every row before anything is written.' })));

  const stage = el('div');
  /**
   * The history lives outside `stage` because `stage` is the wizard, and the
   * wizard is cleared at every step. This is not part of it: it is the standing
   * answer to "what happened to my file", one press away from the screen the
   * file was imported on, for a file imported six weeks ago. It is hidden only
   * while a file is actually being read, when the file in front of you is the
   * only thing that matters.
   */
  const historyHost = el('div');
  host.append(stage, historyHost);
  wireDropTarget();
  void step1();

  /* ------------------------------------------------------ the drop area -- */

  /**
   * The whole view is the drop target, not just the box. `dragenter` and
   * `dragleave` both fire as the pointer crosses every child element, so the
   * highlight is driven by a depth counter rather than by the events alone —
   * without it the outline strobes as the file passes over each tile.
   */
  function wireDropTarget(): void {
    let depth = 0;
    const carriesFiles = (e: DragEvent): boolean =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const paint = (): void => { host.classList.toggle('dragging', depth > 0); };

    host.addEventListener('dragenter', (e) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      depth += 1;
      paint();
    });
    host.addEventListener('dragover', (e) => {
      if (!carriesFiles(e)) return;
      // Without this the browser refuses the drop and opens the file instead.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    });
    host.addEventListener('dragleave', (e) => {
      if (!carriesFiles(e)) return;
      depth = Math.max(0, depth - 1);
      paint();
    });
    host.addEventListener('drop', (e) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      depth = 0;
      paint();
      accept(Array.from(e.dataTransfer?.files ?? []));
    });
  }

  /** Sorts what was dropped into the queue and the politely refused. */
  function accept(files: File[]): void {
    if (files.length === 0) return;
    const taken: File[] = [];
    for (const f of files) {
      const why = refuse(f);
      if (why) outcomes.push({ name: f.name, kind: 'refused', detail: why });
      else taken.push(f);
    }
    queue.push(...taken);

    if (taken.length === 0) {
      toast(`Nothing there could be read. ${outcomes[outcomes.length - 1]?.detail ?? ''}`, 'crit');
      if (idle) void step1();
      return;
    }
    if (!idle) {
      toast(`${taken.length} more file${taken.length === 1 ? '' : 's'} queued.`, 'ok');
      return;
    }
    // Dropping a file starts reading it straight away, because that is what
    // dropping something on a target means. It only gets as far as the preview,
    // which writes nothing — the operator still has to agree before any row is.
    void runQueue();
  }

  function refuse(f: File): string | null {
    if (OK_EXT.test(f.name)) return null;
    const type = (f.type || '').toLowerCase();
    if (type.startsWith('text/') || type === 'application/csv') return null;
    if (/\.(xlsx|xlsm|xls)$/i.test(f.name)) {
      return 'Excel workbooks cannot be read directly. Save it as CSV and drop that.';
    }
    return 'Only CSV, TSV and plain text files can be imported.';
  }

  /* -------------------------------------------------------------- step 1 -- */

  async function step1(): Promise<void> {
    idle = true;
    // Started before the sources are awaited, so the two reads overlap rather
    // than the history waiting on a list it has nothing to do with.
    historyHost.hidden = false;
    void paintHistory();
    if (sources.length === 0) {
      try { sources = await get<Source[]>('/v1/sources'); } catch { sources = []; }
    }
    clear(stage);

    const picker = input({
      type: 'file', multiple: true, accept: '.csv,.tsv,.txt,.tab,.psv,text/csv',
      class: 'sr', tabindex: '-1', 'aria-hidden': 'true',
    });
    picker.addEventListener('change', () => {
      const chosen = Array.from(picker.files ?? []);
      picker.value = '';
      accept(chosen);
    });

    const zone = el('div', {
      class: 'dropzone',
      role: 'button',
      tabindex: '0',
      'aria-label': 'Drop lead files here, or press to choose files from your computer',
    },
      el('div', { class: 'dz-mark', 'aria-hidden': 'true', html: ARROW }),
      el('b', { text: 'Drop your lead files here' }),
      el('span', { text: 'Or press to choose them. CSV, TSV or plain text — comma, semicolon, tab or pipe separated.' }),
      el('span', { class: 'dz-fine', text: 'Any number of files. Any number of rows: a big list is read a piece at a time rather than all at once.' }));

    const openPicker = (): void => picker.click();
    zone.addEventListener('click', openPicker);
    zone.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Enter' || k === ' ') { e.preventDefault(); openPicker(); }
    });

    const paste = el('textarea', {
      class: 'inp', rows: '7',
      style: 'font-family:var(--f-mono);font-size:11.5px',
      placeholder: 'Paste your rows here, starting with the header line.\nfirst_name,last_name,phone,email,state,zip,dob,consent date',
    });
    const usePaste = button('Read the pasted rows', '', () => {
      const text = paste.value.trim();
      if (!text) { toast('There is nothing pasted yet.', 'crit'); return; }
      // Pasted rows become a file so there is exactly one import path, and the
      // preview happens for them too.
      accept([new File([paste.value], 'pasted-list.csv', { type: 'text/csv' })]);
    });

    stage.append(panel('Step 1 of 3 — the files',
      zone,
      picker,
      el('div', { class: 'dz-or', text: 'or paste the rows' }),
      field('Paste', paste),
      usePaste));

    if (queue.length) {
      stage.append(panel(`Queued (${queue.length})`,
        el('div', { class: 'rows' }, ...queue.map((f, i) => row({
          title: f.name,
          sub: fmtBytes(f.size),
          right: button('Remove', 'sm ghost', () => { queue.splice(i, 1); void step1(); }),
        }))),
        el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap;margin-top:12px' },
          button(queue.length === 1 ? 'Read the file' : `Read ${queue.length} files, one after another`, 'pri', () => { void runQueue(); }),
          button('Clear', 'ghost', () => { queue = []; void step1(); }))));
    }

    if (outcomes.length) stage.append(outcomePanel());

    if (sources.length === 0) {
      stage.append(el('div', { class: 'note' },
        el('b', null, 'No lead sources yet. '),
        'You can import without one, but recording where a list came from is what makes cost-per-policy answerable later. ',
        el('a', { href: '#/settings/sources', onclick: (e: Event) => { e.preventDefault(); go('/settings/sources'); } }, 'Add a source')));
    }
  }

  function outcomePanel(): HTMLElement {
    const label: Record<Outcome['kind'], [string, string]> = {
      done: ['imported', 'good'],
      stopped: ['stopped', 'warn'],
      refused: ['not read', 'crit'],
      failed: ['failed', 'crit'],
    };
    return panel('What happened to each file',
      el('div', { class: 'rows' }, ...outcomes.map((o) => row({
        title: o.name,
        sub: o.detail,
        right: chip(label[o.kind][0], label[o.kind][1]),
      }))));
  }

  /* ------------------------------------------------------- what happened -- */

  /**
   * Every import this agency has run, and what became of each one.
   *
   * This exists because an import that quietly dropped four thousand rows looks
   * exactly like one that worked. Same green screen, same "done" — the only
   * thing separating them is a number nobody was ever shown. So this is not a
   * log kept for the sake of it: it is the standing answer to "what happened to
   * my file", and it is one press from the reasons.
   */
  async function paintHistory(refresh = false): Promise<void> {
    if (refresh) history = null;
    const mine = (historySeq += 1);

    if (history === null) {
      clear(historyHost);
      historyHost.append(panel(HISTORY_TITLE, skeleton(3)));
      let loaded: ImportRecord[];
      try {
        loaded = await get<ImportRecord[]>('/v1/imports');
      } catch (e) {
        // A newer load has already painted; this one's failure is stale and
        // saying so would talk over whatever is now on screen.
        if (mine !== historySeq) return;
        clear(historyHost);
        historyHost.append(panel(HISTORY_TITLE,
          el('div', { class: 'warnbox' },
            el('b', null, 'Your import history could not be read. '),
            `${message(e)} Nothing you have imported is affected — this screen simply cannot show it.`),
          el('div', { style: 'margin-top:10px' },
            button('Try again', 'sm', () => { void paintHistory(true); }))));
        toast(`Could not read the import history. ${message(e)}`, 'crit');
        return;
      }
      if (mine !== historySeq) return;
      history = loaded;
    }

    const all = history;
    clear(historyHost);

    if (all.length === 0) {
      historyHost.append(panel(HISTORY_TITLE, empty(
        'Nothing imported yet',
        'Every file you bring in is recorded here — how many rows it held, how many people it added, and every '
        + 'row it would not take, with the reason. Nothing is ever dropped without being written down.')));
      return;
    }

    const shown = historyOpen ? all.length : Math.min(HISTORY_ROWS, all.length);
    const older = all.length - shown;
    historyHost.append(panel(HISTORY_TITLE,
      el('p', {
        class: 'dz-fine', style: 'margin-bottom:10px',
        text: 'Press a file to see exactly what happened to it, including every row it refused and why.',
      }),
      el('div', { class: 'rows' }, ...all.slice(0, shown).map(historyRow)),
      el('div', { style: 'margin-top:10px;display:flex;gap:10px;align-items:center;flex-wrap:wrap' },
        el('span', {
          class: 'dz-fine',
          text: older > 0
            ? `Showing the ${shown} most recent of ${all.length}. ${older} older not shown.`
            : `All ${all.length} shown.`,
        }),
        older > 0
          ? button(`Show all ${all.length}`, 'sm ghost', () => { historyOpen = true; void paintHistory(); })
          : null,
        button('Refresh', 'sm ghost', () => { void paintHistory(true); }))));
  }

  function historyRow(imp: ImportRecord): HTMLElement {
    const st = statusOf(imp.status);
    const right: Node[] = [];
    // The refusal count is the number somebody came here for, so it sits on the
    // row rather than one press further in.
    if (imp.status !== 'preview' && imp.rejected > 0) {
      right.push(chip(`${imp.rejected.toLocaleString()} refused`, 'crit'));
    }
    right.push(chip(st.label, st.chip));
    return row({
      // Built as nodes, not strings: a filename comes from whatever the person
      // dropped on the screen, and `row()` puts a plain string through
      // innerHTML. Same markup, without trusting the file name.
      title: el('div', { class: 't', text: imp.filename }),
      sub: el('div', { class: 's', text: outcomeLine(imp) }),
      right,
      onclick: () => { void openWhatHappened(imp); },
    });
  }

  /**
   * One line of counts, all of them the server's own.
   *
   * A previewed import is the exception and gets none of them. Its stored
   * totals are what the head of the file looked like, not what was written —
   * nothing was written — and printing them next to the word "added" would be
   * the exact lie this screen exists to prevent.
   */
  function outcomeLine(imp: ImportRecord): string {
    const when = ago(imp.committedAt ?? imp.createdAt);
    if (imp.status === 'preview') {
      return `Read for a preview and never imported · ${fmtBytes(imp.byteSize)} · ${when}`;
    }
    return `${imp.inserted.toLocaleString()} added · ${imp.merged.toLocaleString()} merged · `
      + `${imp.rejected.toLocaleString()} refused · of ${imp.totalRows.toLocaleString()} rows read · ${when}`;
  }

  /**
   * One import, explained: the server's counts, what its state actually means,
   * why rows were refused in language an agent can act on, and then every
   * refused row itself.
   */
  async function openWhatHappened(imp: ImportFacts): Promise<void> {
    const body = el('div');
    drawer(`What happened to ${imp.filename}`, body);
    body.append(skeleton(5));

    let rejects: RejectRow[];
    try {
      rejects = await get<RejectRow[]>(`/v1/imports/${imp.id}/rejects`);
    } catch (e) {
      clear(body);
      body.append(el('div', { class: 'warnbox' },
        el('b', null, 'The refused rows could not be read. '),
        `${message(e)} They are still kept — this screen just could not fetch them.`));
      toast(`Could not read the refused rows. ${message(e)}`, 'crit');
      return;
    }
    clear(body);

    const st = statusOf(imp.status);
    if (imp.status !== 'preview') {
      body.append(el('div', { class: 'tiles', style: 'margin-bottom:12px' },
        tile('Rows read', imp.totalRows.toLocaleString(), 'from the file', 'var(--aqua)'),
        tile('Added', imp.inserted.toLocaleString(), 'new people', 'var(--good)'),
        tile('Merged', imp.merged.toLocaleString(), 'into records you had', 'var(--iris)'),
        tile('Refused', imp.rejected.toLocaleString(), 'never arrived',
          imp.rejected ? 'var(--crit)' : 'var(--ink-4)')));
    }
    body.append(el('div', { class: st.box, style: 'margin-bottom:14px' },
      el('b', null, `${st.headline} `), st.blurb));

    if (rejects.length === 0) {
      body.append(imp.rejected > 0
        // The two numbers come from different places — one is the import's own
        // tally, the other is the rows it kept — so they can disagree, and a
        // disagreement here means refused rows went missing. Say so.
        ? el('div', { class: 'warnbox' },
          el('b', null, `This import counted ${imp.rejected.toLocaleString()} refused rows, but kept none of them. `),
          'There is nothing here to review, which should not happen. Tell somebody before you re-import the file.')
        : el('div', { class: 'okbox' },
          el('b', null, imp.status === 'preview'
            ? 'Nothing was written, so nothing was refused. '
            : 'Not one row was refused. '),
          imp.status === 'preview'
            ? 'This file was read for a preview and stopped there.'
            : 'Every row this import read was taken. Nothing was dropped on the way in.'));
      return;
    }

    if (imp.status !== 'preview' && imp.rejected !== rejects.length) {
      body.append(el('div', { class: 'note', style: 'margin-bottom:12px' },
        el('b', null, 'Two counts, and they do not agree. '),
        `The import recorded ${imp.rejected.toLocaleString()} refused rows; `
        + `${rejects.length.toLocaleString()} were kept and are the ones listed here.`));
    }

    const reasons = reasonsOf(rejects);
    if (reasons.length) {
      body.append(panel('Why they were refused',
        el('div', { class: 'rows' }, ...reasons.map((r) => row({
          title: el('div', { class: 't', text: r.title }),
          sub: r.fix ? el('div', { class: 's', text: r.fix }) : undefined,
          right: chip(`${r.count.toLocaleString()} row${r.count === 1 ? '' : 's'}`, 'crit'),
        }))),
        reasons.length > 1
          ? el('div', {
            class: 'dz-fine', style: 'margin-top:10px',
            text: 'A row can fail on more than one of these, so these counts can add up to more than the '
              + 'number of rows below.',
          })
          : null));
    }

    // A file can refuse tens of thousands of rows. They are painted a page at a
    // time, and the page always says how many are still behind it — a list that
    // stops at a hundred without saying so is how somebody concludes only a
    // hundred rows went missing.
    const rowsHost = el('div');
    const foot = el('div', { style: 'display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-top:10px' });
    let showing = Math.min(REJECT_PAGE, rejects.length);

    const paintRejects = (): void => {
      clear(rowsHost);
      rowsHost.append(table(
        ['Row', 'Who it was', 'Phone', 'Where', 'Why it was refused'],
        rejects.slice(0, showing).map((r) => ({ cells: rejectCells(r) })),
      ));
      clear(foot);
      const left = rejects.length - showing;
      foot.append(el('span', {
        class: 'dz-fine',
        text: left === 0
          ? `All ${rejects.length.toLocaleString()} refused rows are shown.`
          : `Showing the first ${showing.toLocaleString()} of ${rejects.length.toLocaleString()}. `
            + `${left.toLocaleString()} more are not shown.`,
      }));
      if (left > 0) {
        foot.append(button(`Show ${Math.min(REJECT_PAGE, left).toLocaleString()} more`, 'sm', () => {
          showing += Math.min(REJECT_PAGE, left);
          paintRejects();
        }));
      }
    };
    paintRejects();

    body.append(panel(`The rows that were refused (${rejects.length.toLocaleString()})`,
      el('p', {
        class: 'dz-fine', style: 'margin-bottom:10px',
        text: 'None of these people are in your book. The row number is where the row sat in the file you sent, '
          + 'so you can find it, fix it, and import the corrected file — anyone already here will merge rather '
          + 'than double up.',
      }),
      rowsHost, foot));
  }

  /**
   * One refused row across the table.
   *
   * Every cell is a text node. All of it is the customer's own file coming back
   * out — including the reject messages, which quote the offending value
   * verbatim ("555-12" is not a usable US number) — and `table()` puts a string
   * cell through innerHTML.
   */
  function rejectCells(r: RejectRow): (string | Node)[] {
    const name = `${r.raw.firstName ?? ''} ${r.raw.lastName ?? ''}`.trim();
    const where = [r.raw.state, r.raw.zip].filter((v) => v && v.trim()).join(' ');
    const errors = r.issues.filter((i) => i.level === 'error');
    const warnings = r.issues.filter((i) => i.level === 'warning');

    const why = el('div');
    // Errors are what refused the row. A row with none — which should not
    // happen — shows everything it has rather than an empty cell.
    for (const i of (errors.length ? errors : r.issues)) why.append(el('div', { text: i.message }));
    if (errors.length && warnings.length) {
      why.append(el('div', {
        style: 'font-size:11px;color:var(--ink-4);margin-top:4px',
        text: `Also noted: ${warnings.map((w) => w.message).join(' ')}`,
      }));
    }

    return [
      el('span', { class: 'mono', text: String(r.rowNumber) }),
      el('div', null,
        el('div', { text: name || 'No name on the row' }),
        r.raw.email
          ? el('div', { style: 'font-size:11px;color:var(--ink-3)', text: r.raw.email })
          : null),
      // Blank precisely when the number is why the row was refused: the
      // importer stores what it managed to read, and it read nothing usable.
      el('span', { class: 'mono', text: r.raw.phone || '—' }),
      el('span', { text: where || '—' }),
      why,
    ];
  }

  /* ---------------------------------------------------- the run, in turn -- */

  async function runQueue(): Promise<void> {
    if (!idle) return;
    let landed = false;
    while (queue.length) {
      const file = queue[0]!;
      idle = false;
      const step = await runOne(file, queue.length);
      // Taken off the front only now, so a file dropped mid-run lands behind it.
      queue.shift();
      if (step === 'landed') { landed = true; break; }
      if (step === 'abandon') break;
    }
    idle = true;
    // Every file in that run added a row to the history, so the copy held here
    // is stale whichever way the run ended.
    history = null;
    // The last file paints its own landing screen; repainting step one over the
    // top of it would throw away the numbers the operator just asked for.
    if (!landed) void step1();
  }

  /** One file, all the way through. Says how the run should carry on. */
  async function runOne(file: File, remaining: number): Promise<Step> {
    const position = `${file.name}${remaining > 1 ? ` — ${remaining - 1} more after this` : ''}`;

    historyHost.hidden = true;
    clear(stage);
    stage.append(panel(`Reading ${file.name}…`,
      el('p', { class: 'dz-fine', text: 'Only the first rows are read for now. Nothing is written yet.' }),
      el('div', { class: 'skeleton', style: 'height:60px;margin-top:10px' })));

    let head: Head;
    try {
      head = await readHead(file);
    } catch (e) {
      return finishFile(file, { name: file.name, kind: 'failed', detail: message(e) });
    }
    if (head.rows.length === 0) {
      return finishFile(file, { name: file.name, kind: 'refused', detail: 'That file has no rows in it.' });
    }

    let preview: ImportPreview;
    try {
      preview = await post<ImportPreview>('/v1/imports/preview', {
        filename: file.name,
        text: head.text,
      });
    } catch (e) {
      return finishFile(file, { name: file.name, kind: 'refused', detail: message(e) });
    }

    return step2(file, preview, head, position);
  }

  function finishFile(file: File, outcome: Outcome): 'next' {
    outcomes.push(outcome);
    if (outcome.kind !== 'done') toast(`${file.name}: ${outcome.detail}`, 'crit');
    return 'next';
  }

  /* -------------------------------------------------------------- step 2 -- */

  function step2(file: File, p: ImportPreview, head: Head, position: string): Promise<Step> {
    return new Promise((resolve) => {
      clear(stage);
      const estimate = estimateRows(file, head, p.headerRow);

      stage.append(panel('Step 2 of 3 — what will happen',
        el('div', { class: 'dz-fine', style: 'margin-bottom:10px', text: position }),
        head.eof
          ? el('div', { class: 'note', style: 'margin-bottom:12px' },
            el('b', null, `The whole file was read: ${estimate.toLocaleString()} rows. `),
            'Everything below covers all of it.')
          : el('div', { class: 'note', style: 'margin-bottom:12px' },
            el('b', null, `This is the first ${p.summary.totalRows.toLocaleString()} rows of about ${estimate.toLocaleString()}. `),
            'The column mapping and the shape of the data are read from these; the rest of the file is sent in batches once you agree, and every row goes through exactly the same checks.'),
        el('div', { class: 'tiles' },
          tile('Rows read so far', p.summary.totalRows.toLocaleString(), head.eof ? 'the whole file' : `of about ${estimate.toLocaleString()}`, 'var(--aqua)'),
          tile('Will be added',
            (p.summary.usable - p.summary.existingDuplicates - p.summary.duplicatesInFile).toLocaleString(),
            'new people', 'var(--good)'),
          tile('Already in the book', String(p.summary.existingDuplicates), 'will merge, not duplicate', 'var(--iris)'),
          tile('Rejected', String(p.summary.rejected), 'listed below', p.summary.rejected ? 'var(--crit)' : 'var(--ink-4)'),
          tile('Duplicates in the file', String(p.summary.duplicatesInFile), '', 'var(--warn)'),
          tile('State from ZIP', String(p.summary.stateFromZip), 'recovered', 'var(--warn)')),
        p.summary.missingConsentDate > 0
          ? el('div', { class: 'note', style: 'margin-top:12px' },
            el('b', null, `${p.summary.missingConsentDate} of those rows have no consent timestamp. `),
            'They will import, but they cannot be dialled until a consent artifact is attached — the gate refuses them.')
          : null));

      // Column mapping, editable, because a vendor will eventually name a column
      // something nobody could have guessed.
      const mapRows = Object.entries(p.mapping).map(([f, idx]) => row({
        title: humanField(f),
        sub: `→ column "${p.header[idx] ?? '?'}"`,
        right: chip(p.mappedBy[f] === 'exact' ? 'exact match' : 'best guess', p.mappedBy[f] === 'exact' ? 'good' : 'warn'),
      }));
      stage.append(panel('How the columns were matched',
        el('div', { class: 'rows' }, ...mapRows),
        p.unmapped.length
          ? el('div', { class: 'note', style: 'margin-top:10px' },
            el('b', null, 'Kept but not mapped: '), p.unmapped.join(', '),
            '. These stay on each lead as extra detail.')
          : null));

      stage.append(panel('The first rows, as they will be stored',
        table(
          ['Row', 'Name', 'Phone', 'Email', 'State', 'Age', ''],
          p.sample.slice(0, 12).map((s) => ({
            // Every cell a Node. This whole row is the contents of somebody's
            // uploaded file — names, phones, emails, state codes — and the
            // history table 180 lines down already learned that lesson.
            cells: [
              mono(String(s.rowNumber)),
              el('span', {
                text: s.lead ? `${s.lead.firstName ?? ''} ${s.lead.lastName ?? ''}`.trim() || '—' : '—',
              }),
              mono(s.lead?.phone ?? '—'),
              el('span', { text: s.lead?.email ?? '—' }),
              s.lead?.state
                ? el('span', null,
                  el('span', { text: s.lead.state }),
                  s.lead.stateSource === 'zip-prefix' ? ' ' : null,
                  s.lead.stateSource === 'zip-prefix' ? chip('from ZIP', 'warn') : null)
                : el('span', { text: '—' }),
              el('span', {
                text: s.lead?.age === null || s.lead?.age === undefined ? '—' : String(s.lead.age),
              }),
              s.usable ? chip('ok', 'good') : chip('rejected', 'crit'),
            ],
          })))));

      if (p.rejects.length) {
        stage.append(panel(`Rejected rows (${p.summary.rejected})`,
          el('p', { style: 'color:var(--ink-3);font-size:12.5px;margin-bottom:10px', text: 'These are kept so you can fix the file and re-import them. Nothing is silently dropped.' }),
          table(['Row', 'Why'], p.rejects.slice(0, 50).map((r) => ({
            // The sharpest one in the file: a reject message quotes the
            // offending cell back verbatim ('"…" is not a usable US number'),
            // so this string carries content from the uploaded file straight
            // into the browser of whoever ran the import. One line per issue,
            // built as nodes rather than joined with <br>.
            cells: [
              mono(String(r.rowNumber)),
              el('div', null, ...r.issues.filter((i) => i.level === 'error')
                .map((i) => el('div', { text: i.message }))),
            ],
          })))));
      }

      const go2 = button('Continue', 'pri');
      go2.addEventListener('click', () => {
        go2.disabled = true;
        void step3(file, p, estimate, position).then(resolve);
      });
      stage.append(el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap' },
        go2,
        button('Skip this file', 'ghost', () => {
          outcomes.push({ name: file.name, kind: 'stopped', detail: 'Skipped before anything was written.' });
          resolve('next');
        }),
        button('Stop the whole run', 'ghost', () => {
          outcomes.push({ name: file.name, kind: 'stopped', detail: 'Skipped before anything was written.' });
          resolve('abandon');
        })));
    });
  }

  /* -------------------------------------------------------------- step 3 -- */

  function step3(file: File, p: ImportPreview, estimate: number, position: string): Promise<Step> {
    return new Promise((resolve) => {
      clear(stage);

      const sourceSel = select([['', 'Not recorded'], ...sources.map((s) => [s.id, s.name] as [string, string])], choice.sourceId);
      const dupe = select([
        ['merge', 'Merge into the existing record (fills gaps, never overwrites)'],
        ['skip', 'Leave the existing record alone'],
      ], choice.onDuplicate);
      const commit = button('Import these leads', 'pri');

      stage.append(panel('Step 3 of 3 — commit',
        el('div', { class: 'dz-fine', style: 'margin-bottom:10px', text: position }),
        field('Where did this list come from?', sourceSel, 'Used for cost per issued policy later.'),
        field('If a lead is already in the book', dupe),
        el('div', { class: 'note', style: 'margin:12px 0' },
          el('b', null, 'Freshly bought lists arrive blocked. '),
          'Every imported number needs a do-not-call scrub before the gate will let it be dialled. You can run one from Compliance the moment this finishes.'),
        commit,
        button('Skip this file', 'ghost', () => {
          outcomes.push({ name: file.name, kind: 'stopped', detail: 'Skipped before anything was written.' });
          resolve('next');
        })));

      commit.addEventListener('click', () => {
        commit.disabled = true;
        commit.textContent = 'Starting…';
        choice.sourceId = sourceSel.value;
        choice.onDuplicate = dupe.value;
        void stream(file, p, estimate).then(resolve);
      });
    });
  }

  /* ------------------------------------------------------- the streaming -- */

  async function stream(file: File, p: ImportPreview, estimate: number): Promise<Step> {
    const ctrl = new AbortController();
    let stopping = false;
    let hardStop = false;
    const started = Date.now();

    let rowsRead = 0;
    let batches = 0;
    let bytes = 0;
    let running: ImportBatchResult | null = null;

    clear(stage);
    const bar = el('i', { style: 'width:0%' });
    const pct = el('b', { class: 'mono', text: '0%' });
    const cells = {
      rows: el('div', { class: 'v', text: '0' }),
      batches: el('div', { class: 'v', text: '0' }),
      bytes: el('div', { class: 'v', text: fmtBytes(0) }),
      added: el('div', { class: 'v', text: '0' }),
      merged: el('div', { class: 'v', text: '0' }),
      eta: el('div', { class: 'v', text: '—' }),
    };
    const stop = button('Stop', 'danger');
    const note = el('p', { class: 'dz-fine', text: 'Rows are sent in batches. Each batch is written whole or not at all, so stopping never leaves half of one behind.' });

    const track = el('div', {
      class: 'bar', role: 'progressbar', 'aria-label': `Importing ${file.name}`,
      'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': '0',
    }, bar);
    stage.append(panel(`Importing ${file.name}`,
      track,
      el('div', { class: 'prog-head' }, pct, el('span', { class: 'dz-fine', text: `about ${estimate.toLocaleString()} rows` })),
      el('div', { class: 'tiles', style: 'margin-top:12px' },
        stat('Rows read', cells.rows),
        stat('Batches sent', cells.batches),
        stat('Read so far', cells.bytes),
        stat('Added', cells.added),
        stat('Merged', cells.merged),
        stat('Time left', cells.eta)),
      note,
      el('div', { style: 'margin-top:12px' }, stop)));

    let painted = 0;
    const paint = (force = false): void => {
      const at = Date.now();
      if (!force && at - painted < 120) return;
      painted = at;
      const done = file.size > 0 ? Math.min(1, bytes / file.size) : 1;
      const whole = Math.round(done * 100);
      bar.style.width = `${(done * 100).toFixed(1)}%`;
      track.setAttribute('aria-valuenow', String(whole));
      pct.textContent = `${whole}%`;
      cells.rows.textContent = rowsRead.toLocaleString();
      cells.batches.textContent = String(batches);
      cells.bytes.textContent = `${fmtBytes(bytes)} of ${fmtBytes(file.size)}`;
      cells.added.textContent = (running?.inserted ?? 0).toLocaleString();
      cells.merged.textContent = (running?.merged ?? 0).toLocaleString();
      const elapsed = at - started;
      cells.eta.textContent = done > 0.01 && done < 1
        ? fmtDuration((elapsed / done) - elapsed)
        : '—';
    };

    stop.addEventListener('click', () => {
      if (!stopping) {
        // The first press stops between batches, so the batch already in flight
        // finishes and the server's counts and ours agree.
        stopping = true;
        stop.textContent = 'Stop now';
        note.textContent = 'Stopping as soon as the batch in flight has finished. Press again to abandon it without waiting.';
        return;
      }
      hardStop = true;
      ctrl.abort();
    });

    let opened: ImportOpened;
    try {
      opened = await post<ImportOpened>(`/v1/imports/${p.importId}/open`, {
        sourceId: choice.sourceId || undefined,
        onDuplicate: choice.onDuplicate,
      });
    } catch (e) {
      return finishFile(file, { name: file.name, kind: 'failed', detail: message(e) });
    }

    const perBatch = Math.max(1, Math.min(BATCH_ROWS, opened.maxRowsPerBatch));
    let skip = p.headerRow + 1;
    let seq = 0;
    let pending: string[] = [];
    let pendingBytes = 0;
    let failure: unknown = null;

    const flush = async (): Promise<void> => {
      if (pending.length === 0) return;
      seq += 1;
      running = await sendBatch(p.importId, seq, pending, ctrl.signal);
      pending = [];
      pendingBytes = 0;
      batches += 1;
      paint(true);
    };

    try {
      for await (const line of fileLines(file, (n) => { bytes += n; }, ctrl.signal)) {
        if (stopping) break;
        if (line.trim().length === 0) continue;
        // The header and anything above it were consumed by the preview.
        if (skip > 0) { skip -= 1; continue; }
        pending.push(line);
        // Characters, not bytes. The threshold is a fiftieth of the router's
        // ceiling, so even a file of four-byte characters cannot reach it.
        pendingBytes += line.length + 1;
        rowsRead += 1;
        if (pending.length >= perBatch || pendingBytes >= BATCH_BYTES) await flush();
        else paint();
      }
      if (!stopping) await flush();
    } catch (e) {
      failure = e;
    }

    if (failure && !hardStop) {
      const detail = message(failure);
      // Whatever landed before the failure is real; the import is closed as
      // cancelled so it can never be mistaken for a completed one.
      const partial = await cancelQuietly(p.importId);
      return finishFile(file, {
        name: file.name,
        kind: 'failed',
        detail: `${detail} ${describe(partial)} were written before it stopped.`,
      });
    }

    if (stopping || hardStop) {
      const partial = await cancelQuietly(p.importId);
      outcomes.push({
        name: file.name,
        kind: 'stopped',
        detail: partial
          ? `Stopped after ${batches} batch${batches === 1 ? '' : 'es'}. ${describe(partial)} were written and kept; nothing was left half done.`
          : 'Stopped. The import could not be closed cleanly — check the import history.',
      });
      toast('Import stopped.', 'crit');
      return hardStop ? 'abandon' : 'next';
    }

    let done: ImportResult;
    try {
      done = await post<ImportResult>(`/v1/imports/${p.importId}/finish`);
    } catch (e) {
      return finishFile(file, { name: file.name, kind: 'failed', detail: message(e) });
    }

    outcomes.push({
      name: file.name,
      kind: 'done',
      detail: `${done.inserted.toLocaleString()} added, ${done.merged.toLocaleString()} merged, ${done.rejected.toLocaleString()} rejected, from ${done.totalRows.toLocaleString()} rows.`,
    });
    toast(`${done.inserted.toLocaleString()} leads imported from ${file.name}.`, 'ok');

    // The last file in the run gets the full landing screen; the ones before it
    // roll straight on, with their outcome kept for the summary at the end.
    if (queue.length <= 1) {
      clear(stage);
      stage.append(panel('Done',
        el('div', { class: 'okbox' },
          el('b', null, `${done.inserted.toLocaleString()} leads added, ${done.merged.toLocaleString()} merged into records you already had.`),
          done.rejected ? ` ${done.rejected.toLocaleString()} rows were rejected and kept for review.` : ''),
        el('div', { class: 'tiles', style: 'margin-top:12px' },
          tile('Added', done.inserted.toLocaleString(), '', 'var(--good)'),
          tile('Merged', done.merged.toLocaleString(), '', 'var(--iris)'),
          tile('Rejected', done.rejected.toLocaleString(), '', 'var(--crit)'),
          tile('Blocked on arrival', done.blockedOnArrival.toLocaleString(), 'need a scrub', 'var(--warn)'),
          tile('Rows', done.totalRows.toLocaleString(), '', 'var(--aqua)'),
          tile('Took', fmtDuration(done.duration_ms), '', 'var(--aqua)')),
        el('div', { style: 'display:flex;gap:8px;margin-top:14px;flex-wrap:wrap' },
          button('Open the book', 'pri', () => go('/leads')),
          // The moment a refusal count is worth reading is the moment it is
          // first shown, so it is a button and not a number to go and find.
          done.rejected
            ? button(`See the ${done.rejected.toLocaleString()} refused rows`, '', () => {
              void openWhatHappened({
                id: done.importId, filename: file.name, status: 'committed',
                totalRows: done.totalRows, inserted: done.inserted,
                merged: done.merged, rejected: done.rejected,
              });
            })
            : null,
          button('Run a scrub now', '', () => go('/compliance')),
          button('Import another list', 'ghost', () => { void step1(); }))));
      if (outcomes.length > 1) stage.append(outcomePanel());
      historyHost.hidden = false;
      void paintHistory(true);
      return 'landed';
    }
    return 'next';
  }

  /**
   * Sends one batch, retrying only what a retry can fix. The sequence number is
   * unchanged on a retry: if the first attempt did land, the server recognises
   * the number and ignores the repeat rather than importing it twice.
   */
  async function sendBatch(importId: string, seq: number, lines: string[], signal: AbortSignal): Promise<ImportBatchResult> {
    let last: unknown;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        return await post<ImportBatchResult>(`/v1/imports/${importId}/rows`, { seq, lines }, { signal });
      } catch (e) {
        last = e;
        const status = e instanceof ApiError ? e.status : 500;
        // A refusal is the server's verdict and says the same thing next time.
        if (e instanceof ApiError && e.code === 'aborted') throw e;
        if (status >= 400 && status < 500) throw e;
        if (attempt === 3) break;
        // A 504 means the server ran out of time, and it may still be
        // finishing the batch that just failed. Backing off for well under a
        // second lands the retry on a server that is still busy; these waits
        // are deliberately long enough for it to have given up too.
        await sleep(status === 504 ? 3000 * attempt : 400 * attempt);
      }
    }
    throw last;
  }

  /** Closes a stopped import. Its counts, not ours, are the honest ones. */
  async function cancelQuietly(importId: string): Promise<ImportResult | null> {
    try {
      return await post<ImportResult>(`/v1/imports/${importId}/cancel`);
    } catch {
      return null;
    }
  }

  function describe(r: ImportResult | null): string {
    if (!r) return 'An unknown number of leads';
    return `${r.inserted.toLocaleString()} added and ${r.merged.toLocaleString()} merged`;
  }

  function stat(k: string, value: HTMLElement): HTMLElement {
    return el('div', { class: 'tile', style: '--accent:var(--aqua)' },
      el('div', { class: 'k', text: k }), value);
  }

  function tile(k: string, v: string, d: string, accent: string): HTMLElement {
    return el('div', { class: 'tile', style: `--accent:${accent}` },
      el('div', { class: 'k', text: k }),
      el('div', { class: 'v', text: v }),
      d ? el('div', { class: 'd', text: d }) : null);
  }

  function humanField(f: string): string {
    return f.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
  }

  return host;
}

/* ------------------------------------------------------- reading a file -- */

interface Head {
  /** The head of the file, verbatim, ready to be previewed. */
  text: string;
  /** Non-blank lines it holds, header and any banner above it included. */
  rows: string[];
  /** Bytes those lines actually weigh — not bytes read, which overshoots. */
  textBytes: number;
  eof: boolean;
}

/**
 * Reads only as much of a file as the preview needs. A quarter-million-row list
 * is never held in memory as one string — not here, and not later: the run that
 * follows re-reads the same file a chunk at a time.
 */
async function readHead(file: File): Promise<Head> {
  const want = PREVIEW_ROWS + PREVIEW_SLACK;
  const rows: string[] = [];
  let bytes = 0;
  let eof = true;
  const ctrl = new AbortController();

  for await (const line of fileLines(file, (n) => { bytes += n; }, ctrl.signal)) {
    if (line.trim().length === 0) continue;
    rows.push(line);
    if (rows.length >= want || bytes >= PREVIEW_MAX_BYTES) { eof = false; break; }
  }
  ctrl.abort();
  const text = rows.join('\n');
  return { text, rows, textBytes: new TextEncoder().encode(text).byteLength, eof };
}

/** Rows in the whole file, extrapolated from the head that was actually read. */
function estimateRows(file: File, head: Head, headerRow: number): number {
  const dataRowsInHead = Math.max(0, head.rows.length - (headerRow + 1));
  if (head.eof || head.textBytes <= 0) return dataRowsInHead;
  // Measured on the lines that were kept rather than on the chunks that were
  // read: the last chunk is only partly used, and counting it whole would put
  // the estimate out by a factor of several.
  const perRow = head.textBytes / Math.max(1, head.rows.length);
  return Math.max(dataRowsInHead, Math.round(file.size / perRow) - (headerRow + 1));
}

/* ---------------------------------------------------------------- bits -- */

const ARROW = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">'
  + '<path d="M12 3v12m0 0 4.5-4.5M12 15l-4.5-4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'
  + '<path d="M4 16v2.5A2.5 2.5 0 0 0 6.5 21h11a2.5 2.5 0 0 0 2.5-2.5V16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

const sleep = (ms: number): Promise<void> => new Promise((r) => { setTimeout(r, ms); });

function message(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong.';
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}
